import { useEffect, useState } from 'react';
import { useBexApi } from '@shared-ui/bex-api-context';

// GraphQl Types
import { ContextInput, DateInput, PropertyDateRangeInput } from 'src/common/__generated__/api/types';

// Hooks
import useCustomerPropertySearchById from '../hooks/useCustomerPropertySearchById';

// Helper
import { checkValue } from 'src/components/common/helper';
import { useStores } from 'src/utils/useStore';

interface HotelDataByIdsProps {
    method: 'useQuery' | 'useStore',
    checkInDate: DateInput,
    checkOutDate: DateInput,
    hotelIds: string[];
    adults?: number;
}

interface iCustomerPropertySearchByIdVariables {
    context: ContextInput;
    dateRange: PropertyDateRangeInput;
    hotelIds: string[];
    criteria?: any; // Making criteria optional
}

/**
 * 
 * @param props HotelDataByIdsProps
 * Data fetch two ways
 * 1) if method useQuery - Get hotel data by useQuery
 * 2) if method useStore - Get hotel data by UseStore
 * @returns 
 */
const HotelDataByIds = (props: HotelDataByIdsProps) => {
    const { method, checkInDate, checkOutDate, hotelIds, adults } = props;
    const { context } = useBexApi();

    if (!checkValue(hotelIds)) {
        return null;
    }

    const hotelIdsArray = hotelIds;
    const propertyDateRangeInput: PropertyDateRangeInput = {
        checkInDate: checkInDate,
        checkOutDate: checkOutDate,
    };

    if (method === 'useQuery') {              
        let customerPropertySearchByIdFullQueryQueryVariables:iCustomerPropertySearchByIdVariables = {
            context,
            dateRange: propertyDateRangeInput,
            hotelIds: hotelIdsArray,                                  
        };
       
        if(checkValue(adults) && !isNaN(Number(adults))){             
            const criteria = {
                "primary": {
                    "dateRange": {
                        checkInDate,
                        checkOutDate
                    },
                    "destination": {
                        "regionName": null,
                        "regionId": null,
                        "coordinates": null,
                        "pinnedPropertyId": null,
                        "excludedPropertyIds": [""],
                        "propertyIds": hotelIdsArray,
                        "mapBounds": null
                    },
                    "rooms": [
                        {
                            "adults":  Number(adults),
                            "children": []
                        }
                    ]
                },
                "secondary": {
                    "booleans": [],
                    "selections": [
                        {
                            "id": "sort",
                            "value": "PRICE_LOW_TO_HIGH"
                        }
                    ],
                    "ranges": []                 
                }
            }

            customerPropertySearchByIdFullQueryQueryVariables = {
                ...customerPropertySearchByIdFullQueryQueryVariables,  // Spread the existing properties
                criteria: {...criteria}// Add new property 'criteria'
            };
        }
       
        const { data, error, loading } = useCustomerPropertySearchById(
            customerPropertySearchByIdFullQueryQueryVariables
        );
        return { data, error, loading };
    } else if (method === 'useStore') {
        const { bexapistore } = useStores();
        const [data, setData] = useState({
            loading: true,
            data: null
        });
        useEffect(() => {
            const getCustomerPropertySearchById = async () => {
                const dataFetched = await bexapistore.fetchCustomerPropertySearchByIdQuery(
                    context,
                    'meso3-hotels-by-ids',
                    propertyDateRangeInput,
                    hotelIdsArray,
                    null
                );
                setData({
                    loading: false,
                    data: dataFetched
                });
            };

            getCustomerPropertySearchById();
        }, []);

        return { data: data?.data, error: '', loading: data?.loading };
    } else {
        return null;
    }
}

export { HotelDataByIds, HotelDataByIdsProps }